/**
 * Timezone-safe date utilities for YUMIX.
 * 
 * CRITICAL: Never use `new Date().toISOString().split('T')[0]` for "today".
 * toISOString() returns UTC, which at 21h EDT = tomorrow in UTC.
 * Always use these helpers instead.
 */

/**
 * Get today's date string (YYYY-MM-DD) in the user's LOCAL timezone.
 * This is the correct way to determine "today" for display and comparisons.
 */
export function getLocalDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get yesterday's date string (YYYY-MM-DD) in local timezone.
 */
export function getYesterdayDateString() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return getLocalDateString(d);
}

/**
 * Get current local hour (0-23). Used for Morning Debrief gate.
 */
export function getLocalHour() {
  return new Date().getHours();
}

/**
 * Check if it's "morning" in local time (between 4h and 11h59).
 */
export function isMorningWindow() {
  const hour = getLocalHour();
  return hour >= 4 && hour < 12;
}

/**
 * Convert a UTC ISO date string to a local date key (YYYY-MM-DD).
 * Use this when grouping meals/logs by day — the meal logged at 22h30 local
 * should stay in "today" even though its ISO string is tomorrow in UTC.
 */
export function utcToLocalDateKey(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return getLocalDateString(date);
}

/**
 * Iter. 406.v — Weekly Battle Plan reset.
 *
 * Returns the ISO date string (YYYY-MM-DD) of the MONDAY of the user's
 * current week, anchored to America/Toronto (so the rollover happens at
 * midnight EDT/EST regardless of the device timezone or DST state).
 *
 * Used as the localStorage key suffix for the 7-Day Battle Plan checkboxes
 * so they automatically reset every Monday morning Eastern Time.
 *
 * Implementation: we use Intl.DateTimeFormat with the 'America/Toronto'
 * timezone to extract the local Y/M/D + weekday, then walk back to Monday.
 * This is DST-safe and doesn't require any third-party library.
 */
export function getWeekIdToronto(date = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  });
  const parts = fmt.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value; return acc;
  }, {});
  // 'short' weekday in en-CA returns Mon, Tue, Wed, Thu, Fri, Sat, Sun.
  const weekdayMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const offset = weekdayMap[parts.weekday] ?? 0;
  // Build a UTC date for the local Y-M-D then subtract `offset` days. Because
  // we only care about the calendar date (no time), UTC math is safe here.
  const localUtc = new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00Z`);
  localUtc.setUTCDate(localUtc.getUTCDate() - offset);
  return localUtc.toISOString().slice(0, 10); // YYYY-MM-DD
}

/**
 * Iter. 406.v+ — Returns the last `count` weekIds in chronological order
 * (oldest → newest, ending with the current week). Used by the dashboard
 * Battle Plan streak rail to render a mini-history of the last N weeks.
 */
export function getRecentWeekIdsToronto(count = 4, date = new Date()) {
  const current = getWeekIdToronto(date);
  const out = [current];
  // Walk back N-1 Mondays. Adding 7 days at a time to a UTC date is safe
  // because Toronto's UTC offset only shifts at 2am local on DST days, never
  // affecting Monday 00:00 UTC math.
  const cursor = new Date(`${current}T00:00:00Z`);
  for (let i = 1; i < count; i++) {
    cursor.setUTCDate(cursor.getUTCDate() - 7);
    out.unshift(cursor.toISOString().slice(0, 10));
  }
  return out;
}

